import { Controller } from '@hotwired/stimulus'

const MAX_SCALE = 3
const DOUBLE_TAP_SCALE = 2.5
const DOUBLE_TAP_DELAY = 300
// Past this much drift it was a swipe, not a tap
const TAP_SLOP = 20

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const point = (touch) => ({ x: touch.clientX, y: touch.clientY })

const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)

const middle = (touches) => ({
  x: (touches[0].clientX + touches[1].clientX) / 2,
  y: (touches[0].clientY + touches[1].clientY) / 2,
})

// Pinch and double tap zoom for one photo; the carousel keeps its swipe until
// the photo is zoomed in.
export default class extends Controller {
  static targets = ['image']

  connect() {
    this.zoomed = false
    this.reset()
  }

  disconnect() {
    this.reset()
  }

  start(event) {
    this.animate(false)
    this.moved = false

    if (event.touches.length === 2) {
      this.pinch = {
        distance: distance(point(event.touches[0]), point(event.touches[1])),
        scale: this.scale,
        offset: { ...this.offset },
        focus: this.fromCenter(middle(event.touches)),
      }
      this.pan = null
      return
    }

    if (event.touches.length !== 1) return

    this.tapStart = point(event.touches[0])
    this.pan = this.zoomed
      ? { start: this.tapStart, offset: { ...this.offset } }
      : null
  }

  move(event) {
    if (this.pinch && event.touches.length === 2) {
      const ratio =
        distance(point(event.touches[0]), point(event.touches[1])) /
        this.pinch.distance
      this.zoomTo(
        clamp(this.pinch.scale * ratio, 1, MAX_SCALE),
        this.pinch.scale,
        this.pinch.focus,
        this.pinch.offset
      )
      this.moved = true
      event.preventDefault()
      return
    }

    if (this.pan && event.touches.length === 1) {
      const current = point(event.touches[0])
      this.apply(this.scale, {
        x: this.pan.offset.x + current.x - this.pan.start.x,
        y: this.pan.offset.y + current.y - this.pan.start.y,
      })
      this.moved = true
      event.preventDefault()
    }
  }

  end(event) {
    if (event.touches.length > 0) return

    this.pinch = null
    this.pan = null

    if (this.scale <= 1) this.reset()
    // A pan armed by the zoomed photo is still a tap when no finger moved
    if (!this.moved) this.detectDoubleTap(event)
  }

  // Also called by the viewer when the slide leaves
  reset() {
    this.scale = 1
    this.offset = { x: 0, y: 0 }
    this.pinch = null
    this.pan = null
    if (this.hasImageTarget) this.imageTarget.style.transform = ''
    this.setZoomed(false)
  }

  detectDoubleTap(event) {
    const released = point(event.changedTouches[0])
    if (this.tapStart && distance(this.tapStart, released) > TAP_SLOP) return

    const previous = this.lastTap
    const now = Date.now()
    this.lastTap = { ...released, at: now }

    if (
      previous &&
      now - previous.at < DOUBLE_TAP_DELAY &&
      distance(previous, released) < TAP_SLOP
    ) {
      this.lastTap = null
      this.toggleZoom(released)
    }
  }

  toggleZoom(at) {
    this.animate(true)

    if (this.zoomed) {
      this.reset()
    } else {
      this.zoomTo(DOUBLE_TAP_SCALE, 1, this.fromCenter(at), { x: 0, y: 0 })
    }
  }

  // Scales around `focus` so the point under the fingers stays where it is
  zoomTo(scale, fromScale, focus, fromOffset) {
    const ratio = scale / fromScale

    this.apply(scale, {
      x: focus.x - ratio * (focus.x - fromOffset.x),
      y: focus.y - ratio * (focus.y - fromOffset.y),
    })
  }

  // A screen point as an offset from the centre the photo scales around
  fromCenter({ x, y }) {
    const bounds = this.element.getBoundingClientRect()

    return {
      x: x - (bounds.left + bounds.width / 2),
      y: y - (bounds.top + bounds.height / 2),
    }
  }

  apply(scale, offset) {
    this.scale = scale
    this.offset = this.withinBounds(scale, offset)
    this.imageTarget.style.transform = `translate3d(${this.offset.x}px, ${this.offset.y}px, 0) scale(${scale})`
    this.setZoomed(scale > 1)
  }

  // Pans only as far as the photo overflows the frame; an axis that fits stays centred
  withinBounds(scale, offset) {
    const limitX = Math.max(
      (this.imageTarget.offsetWidth * scale - this.element.clientWidth) / 2,
      0
    )
    const limitY = Math.max(
      (this.imageTarget.offsetHeight * scale - this.element.clientHeight) / 2,
      0
    )

    return {
      x: clamp(offset.x, -limitX, limitX),
      y: clamp(offset.y, -limitY, limitY),
    }
  }

  setZoomed(zoomed) {
    if (zoomed === this.zoomed) return

    this.zoomed = zoomed
    this.element.classList.toggle('ma-photo-viewer__slide--zoomed', zoomed)
    this.element.dispatchEvent(
      new CustomEvent('photo-zoom:changed', {
        detail: { zoomed },
        bubbles: true,
      })
    )
  }

  animate(enabled) {
    if (!this.hasImageTarget) return

    this.imageTarget.classList.toggle(
      'ma-photo-viewer__image--animated',
      enabled
    )
  }
}
